import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBook,
  faChartLine,
  faCreditCard,
  faFlask,
  faGaugeHigh,
  faKey,
  faLayerGroup,
  faRightFromBracket,
  faShieldHalved,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { playgroundNavIcon } from "../student/playgroundNavIcon";
import { AppSidebarHeader } from "../layout/AppSidebarHeader";
import { usePersistedSidebarCollapsed } from "../layout/usePersistedSidebarCollapsed";
import {
  getTeacherBillingSubscription,
  type TeacherAccessPhase,
  type TeacherBillingSubscriptionResponse,
} from "../auth/api-client";
import { LEGAL_SUPPORT_EMAIL } from "../routes/legal/legal-meta";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { useAuth } from "../auth/AuthContext";

type SidebarItem = {
  to: string;
  label: string;
  icon: IconDefinition;
  end?: boolean;
};

const FULL_SIDEBAR: SidebarItem[] = [
  { to: "/teacher", label: "Overview", icon: faGaugeHigh, end: true },
  { to: "/teacher/lessons", label: "Lessons", icon: faBook },
  { to: "/teacher/courses", label: "Courses", icon: faLayerGroup },
  { to: "/teacher/cohorts", label: "Cohorts", icon: faUsers },
  { to: "/teacher/submissions", label: "Progress", icon: faChartLine },
  { to: "/teacher/playground", label: "Playgrounds", icon: playgroundNavIcon },
  { to: "/teacher/billing", label: "Billing", icon: faCreditCard },
  { to: "/teacher/api-keys", label: "API keys", icon: faKey },
];

const EXPIRED_SIDEBAR: SidebarItem[] = [
  { to: "/teacher/sandbox", label: "Sandbox IDE", icon: faFlask },
  { to: "/teacher/billing", label: "Billing", icon: faCreditCard },
];

const EXPIRED_ALLOWED_PREFIXES = ["/teacher/billing", "/teacher/sandbox", "/admin"];

function buildSidebar(expired: boolean, isAdmin: boolean): SidebarItem[] {
  const base: SidebarItem[] = expired ? [...EXPIRED_SIDEBAR] : [...FULL_SIDEBAR];
  if (isAdmin) {
    const adminItem: SidebarItem = { to: "/admin", label: "Admin", icon: faShieldHalved, end: true };
    if (expired) {
      base.push(adminItem);
    } else {
      const apiKeysIdx = base.findIndex((item) => item.to === "/teacher/api-keys");
      base.splice(apiKeysIdx >= 0 ? apiKeysIdx : base.length, 0, adminItem);
    }
  }
  return base;
}

function isExpiredAllowedPath(pathname: string): boolean {
  return EXPIRED_ALLOWED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}

export default function TeacherLayout() {
  const { identity, signOut, getBearerToken, isAdmin } = useAuth();
  const location = useLocation();
  const bearer = getBearerToken();
  const [accessPhase, setAccessPhase] = useState<TeacherAccessPhase | null>(null);

  const loadAccess = useCallback(async () => {
    if (!bearer) {
      setAccessPhase(null);
      return;
    }
    try {
      const data: TeacherBillingSubscriptionResponse = await getTeacherBillingSubscription(bearer);
      setAccessPhase(data.accessPhase);
    } catch {
      setAccessPhase(null);
    }
  }, [bearer]);

  useEffect(() => {
    void loadAccess();
  }, [loadAccess, location.pathname]);

  const expired = accessPhase === "expired";
  const sidebar = useMemo(() => buildSidebar(expired, isAdmin), [expired, isAdmin]);
  const { collapsed, toggleCollapsed } = usePersistedSidebarCollapsed("ccs-teacher-nav-collapsed");

  if (expired && !isExpiredAllowedPath(location.pathname)) {
    return <Navigate to="/teacher/billing" replace />;
  }

  return (
    <div className={`teacher-app${collapsed ? " teacher-app--nav-collapsed" : ""}`}>
      <aside
        className={`teacher-sidebar${collapsed ? " is-collapsed" : ""}`}
        aria-label="Teacher navigation"
      >
        <AppSidebarHeader
          collapsed={collapsed}
          toggleCollapsed={toggleCollapsed}
          navId="teacher-sidebar-nav"
          workspaceLabel="Teacher Workspace"
          email={identity?.email ?? ""}
        />
        {expired ? (
          <p className="teacher-sidebar-hint muted">
            Your 15-day trial has ended. Use the sandbox IDE or subscribe to unlock teaching tools.
          </p>
        ) : null}
        <nav id="teacher-sidebar-nav" className="teacher-sidebar-nav">
          {sidebar.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end === true}
              className={({ isActive }) => `teacher-nav-item${isActive ? " is-active" : ""}`}
              title={collapsed ? item.label : undefined}
            >
              <FontAwesomeIcon icon={item.icon} className="teacher-nav-icon" fixedWidth />
              <span className="app-sidebar-label">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="app-sidebar-footer teacher-sidebar-footer">
          <p className="app-sidebar-support teacher-sidebar-support">
            Need help?{" "}
            <a href={`mailto:${LEGAL_SUPPORT_EMAIL}`}>{LEGAL_SUPPORT_EMAIL}</a>
          </p>
          <button
            type="button"
            className="btn secondary teacher-sidebar-signout"
            onClick={() => signOut()}
            title={collapsed ? "Sign out" : undefined}
          >
            <FontAwesomeIcon icon={faRightFromBracket} fixedWidth />
            <span className="app-sidebar-label">Sign out</span>
          </button>
        </div>
      </aside>
      <div className="teacher-main">
        <Outlet />
      </div>
    </div>
  );
}
